export type TriviaType = 'music' | 'radio' | 'program' | 'host' | 'sponsored';
export type TriviaMode = 'quick' | 'scheduled' | 'tournament';
export type TriviaStatus = 'active' | 'upcoming' | 'completed' | 'live';
export type TriviaDifficulty = 'easy' | 'medium' | 'hard';

export interface TriviaQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  category: string;
  difficulty: TriviaDifficulty;
  timeLimit: number;
}

export interface TriviaWinner {
  position: number;
  name: string;
  avatarUrl: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  time: string;
}

export interface TriviaBadge {
  id: string;
  name: string;
  emoji: string;
  description: string;
  requirement: string;
  scoreBonus: number;
  color: string;
  bgColor: string;
  iconColor: string;
}

export interface TriviaRankingEntry {
  position: number;
  name: string;
  avatarUrl: string;
  totalScore: number;
  triviasWon: number;
  totalParticipated: number;
  accuracy: number;
  avgTime: string;
  badge?: string;
}

export interface Trivia {
  id: string;
  title: string;
  description: string;
  type: TriviaType;
  mode: TriviaMode;
  status: TriviaStatus;
  startDate: string;
  endDate: string;
  entityName: string;
  entityLogoUrl: string;
  sponsorName?: string;
  sponsorLogoUrl?: string;
  questions: TriviaQuestion[];
  participants: number;
  totalQuestions: number;
  timePerQuestion: number;
  prize: string;
  prizeDescription: string;
  topWinners: TriviaWinner[];
  score: {
    participate: number;
    win: number;
    share: number;
    invite: number;
  };
  tags: string[];
}

export const triviaTypeLabels: Record<TriviaType, string> = {
  music: 'Trivia de Música',
  radio: 'Trivia de Radio',
  program: 'Trivia de Programa',
  host: 'Trivia de Locutor',
  sponsored: 'Trivia Patrocinada',
};

export const triviaTypeIcons: Record<TriviaType, string> = {
  music: 'ri-music-2-line',
  radio: 'ri-radio-line',
  program: 'ri-tv-line',
  host: 'ri-user-voice-line',
  sponsored: 'ri-vip-diamond-line',
};

export const triviaTypeColors: Record<TriviaType, string> = {
  music: 'bg-pink-50 text-pink-700 border-pink-200',
  radio: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  program: 'bg-teal-50 text-teal-700 border-teal-200',
  host: 'bg-amber-50 text-amber-700 border-amber-200',
  sponsored: 'bg-violet-50 text-violet-700 border-violet-200',
};

export const triviaTypeBgLight: Record<TriviaType, string> = {
  music: 'bg-pink-50',
  radio: 'bg-cyan-50',
  program: 'bg-teal-50',
  host: 'bg-amber-50',
  sponsored: 'bg-violet-50',
};

export const triviaTypeTextColors: Record<TriviaType, string> = {
  music: 'text-pink-700',
  radio: 'text-cyan-700',
  program: 'text-teal-700',
  host: 'text-amber-700',
  sponsored: 'text-violet-700',
};

export const triviaTypeGradients: Record<TriviaType, string> = {
  music: 'from-pink-500 to-rose-500',
  radio: 'from-cyan-500 to-blue-500',
  program: 'from-teal-500 to-emerald-500',
  host: 'from-amber-500 to-orange-500',
  sponsored: 'from-violet-500 to-purple-500',
};

export const triviaModeLabels: Record<TriviaMode, string> = {
  quick: 'Rápida',
  scheduled: 'Programada',
  tournament: 'Torneo',
};

export const triviaModeIcons: Record<TriviaMode, string> = {
  quick: 'ri-flashlight-line',
  scheduled: 'ri-calendar-check-line',
  tournament: 'ri-trophy-line',
};

export const triviaModeColors: Record<TriviaMode, string> = {
  quick: 'bg-amber-50 text-amber-700 border-amber-200',
  scheduled: 'bg-sky-50 text-sky-700 border-sky-200',
  tournament: 'bg-amber-100 text-amber-800 border-amber-300',
};

export const triviaStatusLabels: Record<TriviaStatus, string> = {
  active: 'Activa',
  upcoming: 'Próxima',
  completed: 'Completada',
  live: 'En Vivo',
};

export const triviaBadges: TriviaBadge[] = [
  {
    id: 'badge-music-expert',
    name: 'Experto Musical',
    emoji: '🎵',
    description: 'Demostraste un conocimiento excepcional en trivias de música. Ganaste 5 trivias musicales.',
    requirement: 'Ganar 5 Trivias de Música',
    scoreBonus: 500,
    color: 'bg-pink-100 border-pink-300',
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600',
  },
  {
    id: 'badge-rfw-expert',
    name: 'Experto RFW',
    emoji: '📻',
    description: 'Conocés todo sobre las radios, programas y locutores de RFW. Ganaste 10 trivias en total.',
    requirement: 'Ganar 10 Trivias (cualquier tipo)',
    scoreBonus: 1000,
    color: 'bg-cyan-100 border-cyan-300',
    bgColor: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
  },
  {
    id: 'badge-trivia-champion',
    name: 'Campeón de Trivias',
    emoji: '🏆',
    description: 'La élite del conocimiento RFW. Primer lugar en un Torneo de Trivias.',
    requirement: 'Ganar un Torneo de Trivias',
    scoreBonus: 2000,
    color: 'bg-amber-100 border-amber-300',
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-600',
  },
];

export const triviaDashboardStats = {
  totalTrivias: 187,
  activeNow: 8,
  todayLive: 3,
  totalParticipants: 67400,
  totalQuestionsAnswered: 892400,
  avgAccuracy: 72.4,
  avgTimePerQuestion: 8.2,
  prizesAwarded: 234,
  totalScoreDistributed: 124500,
  topType: 'music' as TriviaType,
  currentTournaments: 2,
};

export const triviaGlobalRanking: TriviaRankingEntry[] = [
  { position: 1, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=trivia-carlos-mendoza&orientation=squarish', totalScore: 8450, triviasWon: 12, totalParticipated: 34, accuracy: 91.2, avgTime: '4.2s' },
  { position: 2, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20confident%20smile%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20professional%20headshot%20photography&width=100&height=100&seq=trivia-maria-garcia&orientation=squarish', totalScore: 7890, triviasWon: 9, totalParticipated: 28, accuracy: 88.7, avgTime: '5.1s' },
  { position: 3, name: 'José Hernández', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20his%2030s%20portrait%2C%20friendly%20expression%2C%20warm%20studio%20lighting%2C%20clean%20white%20background%2C%20professional%20headshot&width=100&height=100&seq=trivia-jose-hernandez&orientation=squarish', totalScore: 7230, triviasWon: 7, totalParticipated: 25, accuracy: 86.4, avgTime: '5.8s' },
  { position: 4, name: 'Ana Martínez', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20portrait%2C%20bright%20smile%2C%20modern%20office%20lighting%2C%20sleek%20clean%20background%2C%20editorial%20headshot&width=100&height=100&seq=trivia-ana-martinez&orientation=squarish', totalScore: 6890, triviasWon: 6, totalParticipated: 22, accuracy: 84.9, avgTime: '6.3s' },
  { position: 5, name: 'Pedro López', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20casual%20portrait%2C%20genuine%20smile%2C%20soft%20natural%20window%20light%2C%20relaxed%20atmosphere%2C%20lifestyle%20photography&width=100&height=100&seq=trivia-pedro-lopez&orientation=squarish', totalScore: 6540, triviasWon: 5, totalParticipated: 20, accuracy: 83.1, avgTime: '6.7s' },
  { position: 6, name: 'Laura Sánchez', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20woman%2C%20warm%20expression%2C%20studio%20portrait%20with%20soft%20shadows%2C%20minimal%20aesthetic%2C%20headshot&width=100&height=100&seq=trivia-laura-sanchez&orientation=squarish', totalScore: 6120, triviasWon: 4, totalParticipated: 18, accuracy: 81.5, avgTime: '7.0s' },
  { position: 7, name: 'Diego Ramírez', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20street%20style%20portrait%2C%20confident%20look%2C%20urban%20background%20with%20warm%20tones%2C%20lifestyle%20editorial%20photography&width=100&height=100&seq=trivia-diego-ramirez&orientation=squarish', totalScore: 5890, triviasWon: 3, totalParticipated: 16, accuracy: 79.8, avgTime: '7.4s' },
  { position: 8, name: 'Sofía Herrera', avatarUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20vintage%20inspired%20portrait%2C%20soft%20warm%20lighting%2C%20elegant%20casual%20look%2C%20cream%20background%2C%20editorial%20headshot&width=100&height=100&seq=trivia-sofia-herrera&orientation=squarish', totalScore: 5560, triviasWon: 3, totalParticipated: 15, accuracy: 78.2, avgTime: '7.8s' },
  { position: 9, name: 'Miguel Torres', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20casual%20shirt%2C%20warm%20natural%20light%2C%20friendly%20approachable%20expression%2C%20clean%20background%2C%20portrait%20photography&width=100&height=100&seq=trivia-miguel-torres&orientation=squarish', totalScore: 5230, triviasWon: 2, totalParticipated: 14, accuracy: 76.9, avgTime: '8.1s' },
  { position: 10, name: 'Elena Vargas', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%2C%20modern%20clean%20portrait%2C%20soft%20studio%20lighting%2C%20approachable%20expression%2C%20corporate%20headshot&width=100&height=100&seq=trivia-elena-vargas&orientation=squarish', totalScore: 4980, triviasWon: 2, totalParticipated: 12, accuracy: 75.4, avgTime: '8.5s' },
];

// ─── QUESTIONS ────────────────────────────────────────────

const musicQuestions: TriviaQuestion[] = [
  { id: 'mq-01', question: '¿Quién es conocido como "El Rey del Pop"?', options: ['Michael Jackson', 'Prince', 'Elvis Presley', 'Madonna'], correctAnswer: 0, category: 'Pop', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-02', question: '¿Qué banda lanzó el álbum "The Dark Side of the Moon"?', options: ['Led Zeppelin', 'Pink Floyd', 'The Beatles', 'Queen'], correctAnswer: 1, category: 'Rock', difficulty: 'medium', timeLimit: 15 },
  { id: 'mq-03', question: '¿En qué año se lanzó "Bohemian Rhapsody" de Queen?', options: ['1973', '1975', '1977', '1979'], correctAnswer: 1, category: 'Rock', difficulty: 'hard', timeLimit: 20 },
  { id: 'mq-04', question: '¿Cuál es el nombre real de Bad Bunny?', options: ['Benito Martínez', 'Benito Ocasio', 'Benito Rodríguez', 'Benito García'], correctAnswer: 1, category: 'Urbano', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-05', question: '¿Qué instrumento tocaba Jimi Hendrix?', options: ['Bajo', 'Batería', 'Guitarra', 'Teclado'], correctAnswer: 2, category: 'Rock', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-06', question: '¿De qué país es originario el reggaetón?', options: ['República Dominicana', 'Cuba', 'Puerto Rico', 'Colombia'], correctAnswer: 2, category: 'Urbano', difficulty: 'medium', timeLimit: 15 },
  { id: 'mq-07', question: '¿Qué canción de Shakira fue la más reproducida en 2023?', options: ['Hips Don\'t Lie', 'Waka Waka', 'Shakira: Bzrp Music Sessions 53', 'Te Felicito'], correctAnswer: 2, category: 'Latino', difficulty: 'medium', timeLimit: 15 },
  { id: 'mq-08', question: '¿Quién escribió "Imagine"?', options: ['Paul McCartney', 'John Lennon', 'George Harrison', 'Ringo Starr'], correctAnswer: 1, category: 'Rock', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-09', question: '¿Cuál es el álbum más vendido de todos los tiempos?', options: ['Back in Black - AC/DC', 'The Dark Side of the Moon - Pink Floyd', 'Thriller - Michael Jackson', 'Rumours - Fleetwood Mac'], correctAnswer: 2, category: 'Pop', difficulty: 'hard', timeLimit: 20 },
  { id: 'mq-10', question: '¿Qué grupo es conocido como "Los Cuatro de Liverpool"?', options: ['The Rolling Stones', 'The Who', 'The Beatles', 'Oasis'], correctAnswer: 2, category: 'Rock', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-11', question: '¿En qué género se destaca Daddy Yankee?', options: ['Salsa', 'Bachata', 'Reggaetón', 'Merengue'], correctAnswer: 2, category: 'Urbano', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-12', question: '¿Qué canción de Luis Fonsi y Daddy Yankee rompió récords mundiales?', options: ['Despacito', 'Échame la Culpa', 'Calma', 'Con Calma'], correctAnswer: 0, category: 'Latino', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-13', question: '¿Quién es el vocalista de la banda Coldplay?', options: ['Chris Martin', 'Thom Yorke', 'Bono', 'Dave Grohl'], correctAnswer: 0, category: 'Pop', difficulty: 'medium', timeLimit: 15 },
  { id: 'mq-14', question: '¿Qué género musical popularizó Bob Marley a nivel mundial?', options: ['Ska', 'Reggae', 'Calypso', 'Dancehall'], correctAnswer: 1, category: 'Reggae', difficulty: 'easy', timeLimit: 15 },
  { id: 'mq-15', question: '¿Cuántos integrantes tiene BTS?', options: ['5', '6', '7', '8'], correctAnswer: 2, category: 'K-Pop', difficulty: 'medium', timeLimit: 15 },
];

const radioQuestions: TriviaQuestion[] = [
  { id: 'rq-01', question: '¿Cuál es la frecuencia de Radio Nueva Actitud en RFW?', options: ['94.70 FM', '103.50 FM', '87.01 FM', '200.00 FM'], correctAnswer: 0, category: 'Frecuencias', difficulty: 'medium', timeLimit: 15 },
  { id: 'rq-02', question: '¿Qué radio RFW está dedicada al rock?', options: ['Radio Nueva Actitud', 'Rock Total Radio', 'La Mexicana FM', 'Deportes Total Radio'], correctAnswer: 1, category: 'Radios', difficulty: 'easy', timeLimit: 15 },
  { id: 'rq-03', question: '¿En qué país está basada La Mexicana FM?', options: ['Argentina', 'Colombia', 'México', 'Chile'], correctAnswer: 2, category: 'Radios', difficulty: 'easy', timeLimit: 15 },
  { id: 'rq-04', question: '¿Cuál es la frecuencia más alta registrada en RFW?', options: ['140.00 FM', '200.00 FM', '120.00 FM', '103.50 FM'], correctAnswer: 1, category: 'Frecuencias', difficulty: 'hard', timeLimit: 20 },
  { id: 'rq-05', question: '¿Qué radio RFW transmite desde Estados Unidos?', options: ['Radio Nueva Actitud', 'Radio Fe y Esperanza', 'Deportes Total Radio', 'Banda Radio MX'], correctAnswer: 2, category: 'Radios', difficulty: 'medium', timeLimit: 15 },
  { id: 'rq-06', question: '¿Cuántas radios están registradas en RFW actualmente?', options: ['72', '82', '92', '102'], correctAnswer: 1, category: 'RFW', difficulty: 'hard', timeLimit: 20 },
  { id: 'rq-07', question: '¿Qué significa RFW?', options: ['Radio Free Web', 'Radio Frecuencia RFW', 'Radio Frecuencia World', 'Radio Frequency Web'], correctAnswer: 1, category: 'RFW', difficulty: 'easy', timeLimit: 15 },
  { id: 'rq-08', question: '¿Qué radio RFW es conocida por su programación de banda y regional mexicano?', options: ['Rock Total Radio', 'La Kalle 99.9', 'Banda Radio MX', 'Radio Fe y Esperanza'], correctAnswer: 2, category: 'Radios', difficulty: 'medium', timeLimit: 15 },
  { id: 'rq-09', question: '¿Cuál es la frecuencia de La Kalle?', options: ['94.70 FM', '99.90 FM', '103.50 FM', '107.90 FM'], correctAnswer: 1, category: 'Frecuencias', difficulty: 'hard', timeLimit: 20 },
  { id: 'rq-10', question: '¿Qué radio RFW tiene el mayor RFW Score?', options: ['La Mexicana FM', 'Banda Radio MX', 'Radio Nueva Actitud', 'Rock Total Radio'], correctAnswer: 1, category: 'Rankings', difficulty: 'medium', timeLimit: 15 },
];

const programQuestions: TriviaQuestion[] = [
  { id: 'pq-01', question: '¿Quién conduce el programa "Carrusel Deportivo"?', options: ['Valentina Ríos', 'Fernando Gutiérrez', 'Carlos Mendoza', 'Ricky Navarro'], correctAnswer: 1, category: 'Programas', difficulty: 'medium', timeLimit: 15 },
  { id: 'pq-02', question: '¿Qué programa es conocido como "el noticiero de confianza" de RFW?', options: ['Mañanas Argentinas', 'Noticias 24/7 Radio', 'Voces del Mundo', 'Deporte Total'], correctAnswer: 1, category: 'Programas', difficulty: 'easy', timeLimit: 15 },
  { id: 'pq-03', question: '¿En qué radio se transmite "Tardes con Vale"?', options: ['Radio Nueva Actitud', 'La Mexicana FM', 'Radio Fe y Esperanza', 'Radio Internacional Voz'], correctAnswer: 0, category: 'Programas', difficulty: 'medium', timeLimit: 15 },
  { id: 'pq-04', question: '¿Qué programa tiene el mayor número de seguidores en RFW?', options: ['Carrusel Deportivo', 'Mañanas Argentinas', 'Deporte Total', 'Onda Nocturna'], correctAnswer: 1, category: 'Rankings', difficulty: 'hard', timeLimit: 20 },
  { id: 'pq-05', question: '¿A qué hora empieza "Onda Nocturna"?', options: ['8 PM', '9 PM', '10 PM', '11 PM'], correctAnswer: 2, category: 'Programas', difficulty: 'hard', timeLimit: 20 },
  { id: 'pq-06', question: '¿Qué programa de RFW se enfoca en tecnología y cultura digital?', options: ['Mundo Digital', 'Modo Creativo', 'Voces del Mundo', 'Conexión Global'], correctAnswer: 0, category: 'Programas', difficulty: 'medium', timeLimit: 15 },
  { id: 'pq-07', question: '¿Cuál es el programa matutino más escuchado de RFW?', options: ['Mañanas Argentinas', 'Capital Mix', 'Noticias Regionales', 'Despierta con RFW'], correctAnswer: 0, category: 'Programas', difficulty: 'medium', timeLimit: 15 },
  { id: 'pq-08', question: '¿En qué radio se transmite "Deporte Total"?', options: ['Deportes Total Radio', 'Radio Nueva Actitud', 'Radio Internacional Voz', 'Noticias 24/7 Radio'], correctAnswer: 0, category: 'Programas', difficulty: 'easy', timeLimit: 15 },
];

const hostQuestions: TriviaQuestion[] = [
  { id: 'hq-01', question: '¿Quién es la locutora #1 en el ranking RFW?', options: ['Valentina Ríos', 'Sofía Linares', 'Isabella Torres', 'Camila Ortega'], correctAnswer: 0, category: 'Locutores', difficulty: 'medium', timeLimit: 15 },
  { id: 'hq-02', question: '¿Qué locutor es conocido por su programa en Banda Radio MX?', options: ['Carlos Mendoza', 'Ricky Navarro', 'Diego Quintero', 'Javier Mendoza'], correctAnswer: 1, category: 'Locutores', difficulty: 'medium', timeLimit: 15 },
  { id: 'hq-03', question: '¿Cuál es el RFW Score de Valentina Ríos?', options: ['3,850', '4,120', '2,980', '3,450'], correctAnswer: 0, category: 'Locutores', difficulty: 'hard', timeLimit: 20 },
  { id: 'hq-04', question: '¿Qué locutor tiene la insignia de Campeón de Trivias?', options: ['Carlos Mendoza', 'Ricky Navarro', 'Diego Quintero', 'Javier Mendoza'], correctAnswer: 0, category: 'Locutores', difficulty: 'easy', timeLimit: 15 },
  { id: 'hq-05', question: '¿En qué radio trabaja Diego Quintero?', options: ['Radio Nueva Actitud', 'Banda Radio MX', 'Deportes Total Radio', 'La Kalle 99.9'], correctAnswer: 2, category: 'Locutores', difficulty: 'medium', timeLimit: 15 },
  { id: 'hq-06', question: '¿Quién conduce "Tardes con Vale"?', options: ['Valentina Ríos', 'Camila Ortega', 'Sofía Linares', 'Isabella Torres'], correctAnswer: 0, category: 'Locutores', difficulty: 'easy', timeLimit: 15 },
  { id: 'hq-07', question: '¿Cuál es el eslogan de Ricky Navarro?', options: ['"La música es el idioma que todos entendemos"', '"Hoy es viernes y el cuerpo lo sabe"', '"No apagues la radio"', '"Un aplauso para el oyente"'], correctAnswer: 0, category: 'Locutores', difficulty: 'hard', timeLimit: 20 },
  { id: 'hq-08', question: '¿Qué locutor es también comediante?', options: ['Ricky Navarro', 'Diego Montenegro', 'Javier Solís', 'Roberto Aguilar'], correctAnswer: 1, category: 'Locutores', difficulty: 'medium', timeLimit: 15 },
];

const sponsoredQuestions: TriviaQuestion[] = [
  { id: 'sq-01', question: '¿Cuál es el patrocinador principal de la Copa RFW 2026?', options: ['Banco Santander', 'AutoLux Premium', 'TechNova Solutions', 'Movistar'], correctAnswer: 0, category: 'Patrocinadores', difficulty: 'easy', timeLimit: 15 },
  { id: 'sq-02', question: '¿Qué marca patrocina los eventos deportivos en RFW?', options: ['Gimnasio PowerFit', 'Cerveza Patagonia', 'AutoLux Premium', 'Banco Santander'], correctAnswer: 0, category: 'Patrocinadores', difficulty: 'medium', timeLimit: 15 },
  { id: 'sq-03', question: '¿Qué empresa de tecnología es patrocinador de RFW?', options: ['TechNova Solutions', 'Universidad Virtual', 'Movistar', 'AutoLux Premium'], correctAnswer: 0, category: 'Patrocinadores', difficulty: 'easy', timeLimit: 15 },
  { id: 'sq-04', question: '¿Cuánto invirtió Banco Santander en patrocinios RFW?', options: ['$150,000+', '$210,000+', '$180,000+', '$95,000+'], correctAnswer: 1, category: 'Patrocinadores', difficulty: 'hard', timeLimit: 20 },
  { id: 'sq-05', question: '¿Qué patrocinador es exclusivo de concursos?', options: ['Banco Santander', 'Cerveza Patagonia', 'Librería El Ateneo', 'Hotel Brisas del Pacífico'], correctAnswer: 1, category: 'Patrocinadores', difficulty: 'medium', timeLimit: 15 },
  { id: 'sq-06', question: '¿Cuál es el revenue share estándar de la Red Publicitaria RFW?', options: ['50/50', '60/40 Radio', '70/30 Radio', '40/60 RFW'], correctAnswer: 1, category: 'Publicidad', difficulty: 'medium', timeLimit: 15 },
  { id: 'sq-07', question: '¿Cuántos patrocinadores activos tiene RFW?', options: ['8', '10', '12', '15'], correctAnswer: 1, category: 'RFW', difficulty: 'hard', timeLimit: 20 },
  { id: 'sq-08', question: '¿Qué tipo de patrocinio ofrece el tier "Patrocinado por"?', options: ['Mención básica', 'Logo destacado', 'Exclusividad total con naming', 'Sello oficial del evento'], correctAnswer: 2, category: 'Patrocinadores', difficulty: 'medium', timeLimit: 15 },
];

// ─── TRIVIAS ─────────────────────────────────────────────

export const rfwTrivias: Trivia[] = [
  {
    id: 'trivia-001',
    title: 'Duelo de Décadas: 80s vs 90s',
    description: '¿Cuánto sabés de la música que definió dos décadas? Respondé 10 preguntas sobre los hits más icónicos de los 80 y 90. El top 3 gana insignias exclusivas.',
    type: 'music',
    mode: 'quick',
    status: 'live',
    startDate: '2026-06-21T20:00:00Z',
    endDate: '2026-06-21T21:00:00Z',
    entityName: 'Radio Nueva Actitud',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=trivia-logo-nueva-actitud&orientation=squarish',
    questions: musicQuestions.slice(0, 10),
    participants: 342,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Experto en Décadas',
    prizeDescription: 'Top 3 ganan insignia exclusiva + 500 pts RFW. El ganador recibe mención al aire en Radio Nueva Actitud.',
    topWinners: [],
    score: { participate: 25, win: 500, share: 15, invite: 25 },
    tags: ['música', 'décadas', 'rápida'],
  },
  {
    id: 'trivia-002',
    title: 'Conocé Tu Radio: Rock Total',
    description: 'Trivia especial sobre la historia, programas, locutores y momentos épicos de Rock Total Radio. Solo para verdaderos fans del rock.',
    type: 'radio',
    mode: 'scheduled',
    status: 'active',
    startDate: '2026-06-22T12:00:00Z',
    endDate: '2026-06-22T18:00:00Z',
    entityName: 'Rock Total Radio',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=trivia-logo-rock-total&orientation=squarish',
    questions: radioQuestions.slice(0, 10),
    participants: 567,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Fan de Rock Total',
    prizeDescription: 'El ganador recibe insignia exclusiva "Fan de Rock Total" + 300 pts RFW + mención especial en el club oficial.',
    topWinners: [],
    score: { participate: 25, win: 300, share: 15, invite: 25 },
    tags: ['radio', 'rock', 'club'],
  },
  {
    id: 'trivia-003',
    title: 'La Voz del Deporte',
    description: '¿Cuánto sabés de los programas deportivos de RFW? Carrusel Deportivo, Deporte Total y más. Poné a prueba tu conocimiento deportivo.',
    type: 'program',
    mode: 'scheduled',
    status: 'active',
    startDate: '2026-06-23T10:00:00Z',
    endDate: '2026-06-23T20:00:00Z',
    entityName: 'Deportes Total Radio',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=100&height=100&seq=trivia-logo-deportes-total&orientation=squarish',
    questions: programQuestions.slice(0, 8),
    participants: 234,
    totalQuestions: 8,
    timePerQuestion: 20,
    prize: 'Insignia Experto Deportivo',
    prizeDescription: 'Top 3 ganan insignia "Experto Deportivo" + 250 pts RFW. Ganador recibe mención en vivo en Carrusel Deportivo.',
    topWinners: [],
    score: { participate: 25, win: 250, share: 15, invite: 25 },
    tags: ['deportes', 'programa', 'competencia'],
  },
  {
    id: 'trivia-004',
    title: 'Ricky Navarro: El Desafío',
    description: 'Trivia dedicada al locutor #1 de Banda Radio MX. Frases célebres, momentos históricos al aire, datos curiosos y su trayectoria en RFW.',
    type: 'host',
    mode: 'quick',
    status: 'active',
    startDate: '2026-06-21T18:00:00Z',
    endDate: '2026-06-21T19:30:00Z',
    entityName: 'Ricky Navarro',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%2C%20warm%20smile%2C%20professional%20studio%20setting%2C%20golden%20lighting%2C%20clean%20background%2C%20editorial%20photography&width=100&height=100&seq=trivia-ricky-navarro&orientation=squarish',
    questions: hostQuestions.slice(0, 8),
    participants: 189,
    totalQuestions: 8,
    timePerQuestion: 15,
    prize: 'Saludo en Vivo de Ricky',
    prizeDescription: 'El ganador recibe un saludo personalizado de Ricky Navarro al aire + insignia "Fan de Ricky" + 200 pts',
    topWinners: [],
    score: { participate: 25, win: 200, share: 15, invite: 25 },
    tags: ['locutor', 'rápida', 'fan'],
  },
  {
    id: 'trivia-005',
    title: 'Copa del Conocimiento RFW',
    description: 'Torneo patrocinado por Banco Santander. 32 participantes, 5 rondas, eliminación directa. El campeón se lleva un premio especial y la insignia Campeón de Trivias.',
    type: 'sponsored',
    mode: 'tournament',
    status: 'active',
    startDate: '2026-06-24T14:00:00Z',
    endDate: '2026-06-24T20:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    sponsorName: 'Banco Santander',
    sponsorLogoUrl: 'https://readdy.ai/api/search-image?query=Bank%20logo%20design%2C%20circular%20emblem%20with%20professional%20corporate%20identity%2C%20red%20and%20white%20colors%2C%20modern%20financial%20brand&width=100&height=100&seq=trivia-logo-santander&orientation=squarish',
    questions: [...musicQuestions, ...radioQuestions, ...programQuestions, ...hostQuestions, ...sponsoredQuestions].slice(0, 20),
    participants: 32,
    totalQuestions: 20,
    timePerQuestion: 12,
    prize: '🏆 Campeón de Trivias + $500 Premio',
    prizeDescription: 'Primer lugar: Insignia Campeón de Trivias + $500 USD en premio de Banco Santander + 2,000 pts RFW. Segundo: insignia Subcampeón + $200 USD + 1,000 pts. Tercero: insignia + $100 USD + 500 pts.',
    topWinners: [],
    score: { participate: 50, win: 2000, share: 15, invite: 35 },
    tags: ['torneo', 'patrocinado', 'élite'],
  },
  {
    id: 'trivia-006',
    title: 'Ritmos Latinos: Del Ayer al Hoy',
    description: 'Desde la salsa clásica hasta el reggaetón moderno. 12 preguntas sobre la evolución de la música latina. ¿Sos un verdadero experto?',
    type: 'music',
    mode: 'scheduled',
    status: 'active',
    startDate: '2026-06-22T16:00:00Z',
    endDate: '2026-06-23T16:00:00Z',
    entityName: 'La Kalle 99.9',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=trivia-logo-la-kalle&orientation=squarish',
    questions: musicQuestions.slice(5, 15).concat(musicQuestions.slice(0, 2)),
    participants: 456,
    totalQuestions: 12,
    timePerQuestion: 15,
    prize: 'Insignia Ritmo Latino',
    prizeDescription: 'Ganador: Insignia "Ritmo Latino" + 400 pts RFW + playlist curada por DJ de La Kalle con tu nombre.',
    topWinners: [],
    score: { participate: 30, win: 400, share: 15, invite: 25 },
    tags: ['música', 'latino', 'urbano'],
  },
  {
    id: 'trivia-007',
    title: 'Mañanas Argentinas: Edición Especial',
    description: 'El programa más escuchado de RFW te desafía. Preguntas sobre los momentos más memorables, invitados y datos curiosos del programa.',
    type: 'program',
    mode: 'quick',
    status: 'active',
    startDate: '2026-06-21T21:00:00Z',
    endDate: '2026-06-21T22:00:00Z',
    entityName: 'Mañanas Argentinas',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Morning%20show%20program%20logo%20design%2C%20circular%20emblem%20with%20sun%20and%20coffee%20motifs%2C%20warm%20energetic%20colors%2C%20friendly%20morning%20broadcast%20identity&width=100&height=100&seq=trivia-logo-mañanas-argentinas&orientation=squarish',
    questions: programQuestions.slice(0, 8),
    participants: 278,
    totalQuestions: 8,
    timePerQuestion: 15,
    prize: 'Saludo al Aire en Mañanas Argentinas',
    prizeDescription: 'Ganador: Saludo en vivo durante el programa + insignia "Fan de Mañanas Argentinas" + 200 pts RFW.',
    topWinners: [],
    score: { participate: 25, win: 200, share: 15, invite: 25 },
    tags: ['programa', 'rápida', 'especial'],
  },
  {
    id: 'trivia-008',
    title: 'Conocé La Mexicana FM',
    description: 'Todo lo que siempre quisiste saber sobre la radio más mexicana de RFW. Historia, locutores, clubes, concursos y más.',
    type: 'radio',
    mode: 'scheduled',
    status: 'upcoming',
    startDate: '2026-06-25T10:00:00Z',
    endDate: '2026-06-26T10:00:00Z',
    entityName: 'La Mexicana FM',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=trivia-logo-mexicana-fm&orientation=squarish',
    questions: radioQuestions.slice(0, 10),
    participants: 0,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Fan de La Mexicana FM',
    prizeDescription: 'Ganador: Insignia exclusiva + 300 pts RFW + mención en el club oficial.',
    topWinners: [],
    score: { participate: 25, win: 300, share: 15, invite: 25 },
    tags: ['radio', 'regional', 'cultura'],
  },
  {
    id: 'trivia-009',
    title: 'Valentina Ríos: La Reina del Micro',
    description: 'Trivia sobre la trayectoria de la locutora #1 de RFW. Desde sus inicios hasta hoy, ¿cuánto sabés de Vale?',
    type: 'host',
    mode: 'scheduled',
    status: 'upcoming',
    startDate: '2026-06-26T15:00:00Z',
    endDate: '2026-06-26T21:00:00Z',
    entityName: 'Valentina Ríos',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%2C%20confident%20warm%20smile%2C%20studio%20with%20microphones%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography&width=100&height=100&seq=trivia-valentina-rios&orientation=squarish',
    questions: hostQuestions.slice(2, 10),
    participants: 0,
    totalQuestions: 8,
    timePerQuestion: 15,
    prize: 'Video Saludo de Valentina Ríos',
    prizeDescription: 'Ganador: Video personalizado de Valentina + insignia "ValeFan" + 250 pts RFW.',
    topWinners: [],
    score: { participate: 25, win: 250, share: 15, invite: 25 },
    tags: ['locutor', 'fan', 'especial'],
  },
  {
    id: 'trivia-010',
    title: 'Liga de Campeones RFW — Julio 2026',
    description: 'El torneo mensual más esperado. 64 participantes, 6 rondas, preguntas de todas las categorías. Patrocinado por AutoLux Premium. El campeón se lleva un premio espectacular.',
    type: 'sponsored',
    mode: 'tournament',
    status: 'upcoming',
    startDate: '2026-07-01T10:00:00Z',
    endDate: '2026-07-01T22:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    sponsorName: 'AutoLux Premium',
    sponsorLogoUrl: 'https://readdy.ai/api/search-image?query=Luxury%20car%20dealership%20logo%20design%2C%20circular%20emblem%20with%20elegant%20chrome%20details%2C%20premium%20automotive%20brand%20identity%2C%20sophisticated%20dark%20tones&width=100&height=100&seq=trivia-logo-autolux&orientation=squarish',
    questions: [...musicQuestions, ...radioQuestions, ...programQuestions, ...hostQuestions, ...sponsoredQuestions].slice(0, 25),
    participants: 0,
    totalQuestions: 25,
    timePerQuestion: 10,
    prize: '🏆 Campeón RFW Julio + iPhone 16 Pro',
    prizeDescription: '1er lugar: Insignia Campeón de Trivias + iPhone 16 Pro de AutoLux Premium + 3,000 pts. 2do: Insignia Subcampeón + AirPods Pro + 1,500 pts. 3ro: Insignia + $200 USD gift card + 800 pts.',
    topWinners: [],
    score: { participate: 75, win: 3000, share: 15, invite: 50 },
    tags: ['torneo', 'patrocinado', 'mensual', 'élite'],
  },
  {
    id: 'trivia-011',
    title: 'El ABC del Reguetón',
    description: 'Desde sus orígenes en Puerto Rico hasta la dominación global. 10 preguntas que solo un verdadero fan del género puede responder.',
    type: 'music',
    mode: 'scheduled',
    status: 'upcoming',
    startDate: '2026-06-27T18:00:00Z',
    endDate: '2026-06-28T18:00:00Z',
    entityName: 'La Kalle 99.9',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=trivia-logo-la-kalle&orientation=squarish',
    questions: musicQuestions.slice(4, 11).concat(musicQuestions.slice(11, 14)),
    participants: 0,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Rey del Perreo',
    prizeDescription: 'Ganador: Insignia "Rey del Perreo" + 350 pts RFW.',
    topWinners: [],
    score: { participate: 25, win: 350, share: 15, invite: 25 },
    tags: ['música', 'urbano', 'cultura'],
  },
  {
    id: 'trivia-012',
    title: 'Desafío RFW: Las Reglas del Juego',
    description: '¿Conocés todas las reglas, puntuaciones y mecánicas de RFW? Desde el Score hasta la Reputación, pasando por Copas, Competencias y más. Para verdaderos expertos de la plataforma.',
    type: 'radio',
    mode: 'scheduled',
    status: 'upcoming',
    startDate: '2026-06-28T12:00:00Z',
    endDate: '2026-06-29T12:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    questions: [...radioQuestions.slice(5, 10), ...sponsoredQuestions.slice(2, 7)],
    participants: 0,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Maestro RFW',
    prizeDescription: 'Ganador: Insignia "Maestro RFW" + 500 pts RFW. Top 3: insignia "Conocedor RFW" + 300 pts.',
    topWinners: [],
    score: { participate: 25, win: 500, share: 15, invite: 25 },
    tags: ['rfw', 'reglas', 'expertos'],
  },
  {
    id: 'trivia-013',
    title: 'Batalla de Bandas: Rock Argentino vs Mexicano',
    description: 'Dos países, un solo género, 10 preguntas. ¿De qué lado estás? La banda más votada recibe un especial de 1 hora en su radio.',
    type: 'music',
    mode: 'quick',
    status: 'completed',
    startDate: '2026-06-20T20:00:00Z',
    endDate: '2026-06-20T21:30:00Z',
    entityName: 'Rock Total Radio',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=trivia-logo-rock-total&orientation=squarish',
    questions: musicQuestions.slice(0, 10),
    participants: 412,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Rockstar',
    prizeDescription: 'Ganador: Insignia "Rockstar" + 400 pts RFW.',
    topWinners: [
      { position: 1, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-carlos-mendoza&orientation=squarish', score: 920, correctAnswers: 10, totalQuestions: 10, time: '1m 42s' },
      { position: 2, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20confident%20smile%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20professional%20headshot%20photography&width=100&height=100&seq=winner-maria-garcia&orientation=squarish', score: 850, correctAnswers: 9, totalQuestions: 10, time: '2m 18s' },
      { position: 3, name: 'José Hernández', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20his%2030s%20portrait%2C%20friendly%20expression%2C%20warm%20studio%20lighting%2C%20clean%20white%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-jose-hernandez&orientation=squarish', score: 780, correctAnswers: 8, totalQuestions: 10, time: '3m 05s' },
    ],
    score: { participate: 25, win: 400, share: 15, invite: 25 },
    tags: ['música', 'rock', 'batalla'],
  },
  {
    id: 'trivia-014',
    title: '¿Cuánto Sabés de Tu Locutor Favorito?',
    description: 'Trivia combinada sobre los 5 locutores más populares de RFW. Preguntas sobre sus carreras, frases y momentos memorables.',
    type: 'host',
    mode: 'scheduled',
    status: 'completed',
    startDate: '2026-06-19T14:00:00Z',
    endDate: '2026-06-20T14:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    questions: [...hostQuestions.slice(0, 6), ...hostQuestions.slice(6, 8)],
    participants: 534,
    totalQuestions: 8,
    timePerQuestion: 15,
    prize: 'Insignia Fanático de Locutores',
    prizeDescription: 'Ganador: Insignia "Fanático de Locutores" + 350 pts RFW.',
    topWinners: [
      { position: 1, name: 'Ana Martínez', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20portrait%2C%20bright%20smile%2C%20modern%20office%20lighting%2C%20sleek%20clean%20background%2C%20editorial%20headshot&width=100&height=100&seq=winner-ana-martinez&orientation=squarish', score: 780, correctAnswers: 8, totalQuestions: 8, time: '3m 12s' },
      { position: 2, name: 'Diego Ramírez', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20street%20style%20portrait%2C%20confident%20look%2C%20urban%20background%20with%20warm%20tones%2C%20lifestyle%20editorial%20photography&width=100&height=100&seq=winner-diego-ramirez&orientation=squarish', score: 710, correctAnswers: 7, totalQuestions: 8, time: '4m 01s' },
      { position: 3, name: 'Sofía Herrera', avatarUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20vintage%20inspired%20portrait%2C%20soft%20warm%20lighting%2C%20elegant%20casual%20look%2C%20cream%20background%2C%20editorial%20headshot&width=100&height=100&seq=winner-sofia-herrera&orientation=squarish', score: 650, correctAnswers: 6, totalQuestions: 8, time: '5m 20s' },
    ],
    score: { participate: 25, win: 350, share: 15, invite: 25 },
    tags: ['locutor', 'fan', 'combinado'],
  },
  {
    id: 'trivia-015',
    title: 'Torneo de Primavera RFW 2026',
    description: 'El torneo que consagró a Carlos Mendoza como el primer Campeón de Trivias. 64 participantes, 6 rondas, preguntas de élite. Patrocinado por TechNova Solutions.',
    type: 'sponsored',
    mode: 'tournament',
    status: 'completed',
    startDate: '2026-06-14T10:00:00Z',
    endDate: '2026-06-14T20:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    sponsorName: 'TechNova Solutions',
    questions: [...musicQuestions, ...radioQuestions, ...programQuestions, ...hostQuestions, ...sponsoredQuestions].slice(0, 25),
    participants: 64,
    totalQuestions: 25,
    timePerQuestion: 12,
    prize: '🏆 Campeón de Trivias + Notebook Gamer',
    prizeDescription: '1er lugar: Carlos Mendoza — Insignia Campeón de Trivias + Notebook Gamer de TechNova + 2,000 pts. 2do: María García — Subcampeón + Tablet + 1,500 pts. 3ro: José Hernández — Gift Card $200 + 1,000 pts.',
    topWinners: [
      { position: 1, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-carlos-mendoza-2&orientation=squarish', score: 2450, correctAnswers: 24, totalQuestions: 25, time: '4m 15s' },
      { position: 2, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20confident%20smile%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20professional%20headshot%20photography&width=100&height=100&seq=winner-maria-garcia-2&orientation=squarish', score: 2180, correctAnswers: 22, totalQuestions: 25, time: '5m 30s' },
      { position: 3, name: 'José Hernández', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20his%2030s%20portrait%2C%20friendly%20expression%2C%20warm%20studio%20lighting%2C%20clean%20white%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-jose-hernandez-2&orientation=squarish', score: 1950, correctAnswers: 20, totalQuestions: 25, time: '6m 10s' },
    ],
    score: { participate: 75, win: 2000, share: 15, invite: 50 },
    tags: ['torneo', 'patrocinado', 'histórico'],
  },
  {
    id: 'trivia-016',
    title: 'Especial Bandas de los 2000s',
    description: 'Linkin Park, Green Day, My Chemical Romance, Evanescence... la época dorada del rock alternativo. 10 preguntas para los que crecieron con MTV.',
    type: 'music',
    mode: 'scheduled',
    status: 'completed',
    startDate: '2026-06-18T16:00:00Z',
    endDate: '2026-06-19T16:00:00Z',
    entityName: 'Rock Total Radio',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=trivia-logo-rock-total&orientation=squarish',
    questions: musicQuestions.slice(5, 15),
    participants: 623,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Nostalgia 2000',
    prizeDescription: 'Ganador: Insignia "Nostalgia 2000" + 400 pts RFW.',
    topWinners: [
      { position: 1, name: 'Pedro López', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20casual%20portrait%2C%20genuine%20smile%2C%20soft%20natural%20window%20light%2C%20relaxed%20atmosphere%2C%20lifestyle%20photography&width=100&height=100&seq=winner-pedro-lopez&orientation=squarish', score: 890, correctAnswers: 9, totalQuestions: 10, time: '2m 50s' },
      { position: 2, name: 'Laura Sánchez', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20woman%2C%20warm%20expression%2C%20studio%20portrait%20with%20soft%20shadows%2C%20minimal%20aesthetic%2C%20headshot&width=100&height=100&seq=winner-laura-sanchez&orientation=squarish', score: 820, correctAnswers: 8, totalQuestions: 10, time: '3m 45s' },
      { position: 3, name: 'Miguel Torres', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20casual%20shirt%2C%20warm%20natural%20light%2C%20friendly%20approachable%20expression%2C%20clean%20background%2C%20portrait%20photography&width=100&height=100&seq=winner-miguel-torres&orientation=squarish', score: 760, correctAnswers: 8, totalQuestions: 10, time: '4m 20s' },
    ],
    score: { participate: 25, win: 400, share: 15, invite: 25 },
    tags: ['música', 'rock', 'nostalgia'],
  },
  {
    id: 'trivia-017',
    title: 'Noche de Leyendas: Freddie Mercury',
    description: 'Homenaje al vocalista más grande de la historia del rock. Preguntas sobre su vida, Queen, sus shows icónicos y legado eterno.',
    type: 'music',
    mode: 'quick',
    status: 'completed',
    startDate: '2026-06-17T21:00:00Z',
    endDate: '2026-06-17T22:00:00Z',
    entityName: 'Radio Nueva Actitud',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=trivia-logo-nueva-actitud&orientation=squarish',
    questions: musicQuestions.slice(2, 10).concat(musicQuestions.slice(0, 2)),
    participants: 345,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Leyenda del Rock',
    prizeDescription: 'Ganador: Insignia "Leyenda del Rock" + 500 pts RFW.',
    topWinners: [
      { position: 1, name: 'Elena Vargas', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%2C%20modern%20clean%20portrait%2C%20soft%20studio%20lighting%2C%20approachable%20expression%2C%20corporate%20headshot&width=100&height=100&seq=winner-elena-vargas&orientation=squarish', score: 910, correctAnswers: 10, totalQuestions: 10, time: '2m 05s' },
      { position: 2, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-carlos-mendoza-3&orientation=squarish', score: 850, correctAnswers: 9, totalQuestions: 10, time: '3m 12s' },
      { position: 3, name: 'Ana Martínez', avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20portrait%2C%20bright%20smile%2C%20modern%20office%20lighting%2C%20sleek%20clean%20background%2C%20editorial%20headshot&width=100&height=100&seq=winner-ana-martinez-2&orientation=squarish', score: 790, correctAnswers: 8, totalQuestions: 10, time: '4m 35s' },
    ],
    score: { participate: 25, win: 500, share: 15, invite: 25 },
    tags: ['música', 'rock', 'homenaje'],
  },
  {
    id: 'trivia-018',
    title: 'Reto Banda: Conocé Tu Música',
    description: 'Trivia patrocinada por Cerveza Patagonia. Preguntas sobre los grandes exponentes de la música regional mexicana, banda, norteño y más.',
    type: 'sponsored',
    mode: 'scheduled',
    status: 'completed',
    startDate: '2026-06-16T12:00:00Z',
    endDate: '2026-06-17T12:00:00Z',
    entityName: 'Banda Radio MX',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=trivia-logo-banda-mx&orientation=squarish',
    sponsorName: 'Cerveza Patagonia',
    questions: musicQuestions.slice(6, 14).concat(hostQuestions.slice(1, 3)),
    participants: 289,
    totalQuestions: 10,
    timePerQuestion: 15,
    prize: 'Insignia Experto Banda + 6-Pack Patagonia',
    prizeDescription: 'Ganador: Insignia "Experto Banda" + 6-Pack de Cerveza Patagonia + 300 pts RFW.',
    topWinners: [
      { position: 1, name: 'Diego Ramírez', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20street%20style%20portrait%2C%20confident%20look%2C%20urban%20background%20with%20warm%20tones%2C%20lifestyle%20editorial%20photography&width=100&height=100&seq=winner-diego-ramirez-2&orientation=squarish', score: 850, correctAnswers: 9, totalQuestions: 10, time: '3m 15s' },
      { position: 2, name: 'Sofía Herrera', avatarUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20vintage%20inspired%20portrait%2C%20soft%20warm%20lighting%2C%20elegant%20casual%20look%2C%20cream%20background%2C%20editorial%20headshot&width=100&height=100&seq=winner-sofia-herrera-2&orientation=squarish', score: 780, correctAnswers: 8, totalQuestions: 10, time: '4m 10s' },
      { position: 3, name: 'Miguel Torres', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20in%20casual%20shirt%2C%20warm%20natural%20light%2C%20friendly%20approachable%20expression%2C%20clean%20background%2C%20portrait%20photography&width=100&height=100&seq=winner-miguel-torres-2&orientation=squarish', score: 720, correctAnswers: 7, totalQuestions: 10, time: '5m 25s' },
    ],
    score: { participate: 25, win: 300, share: 15, invite: 25 },
    tags: ['patrocinado', 'regional', 'banda'],
  },
  {
    id: 'trivia-019',
    title: 'La Guerra de los Programas',
    description: 'Trivia sobre los programas más icónicos de RFW. Preguntas de Carrusel Deportivo, Mañanas Argentinas, Deporte Total, Onda Nocturna y más.',
    type: 'program',
    mode: 'scheduled',
    status: 'completed',
    startDate: '2026-06-15T14:00:00Z',
    endDate: '2026-06-16T14:00:00Z',
    entityName: 'RFW Central',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20network%20logo%20design%2C%20circular%20emblem%20with%20broadcast%20waves%2C%20modern%20corporate%20identity%2C%20elegant%20gold%20and%20dark%20tones&width=100&height=100&seq=trivia-logo-rfw-central&orientation=squarish',
    questions: programQuestions.slice(0, 8),
    participants: 412,
    totalQuestions: 8,
    timePerQuestion: 15,
    prize: 'Insignia Coleccionista de Programas',
    prizeDescription: 'Ganador: Insignia "Coleccionista de Programas" + 350 pts RFW.',
    topWinners: [
      { position: 1, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20confident%20smile%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20professional%20headshot%20photography&width=100&height=100&seq=winner-maria-garcia-3&orientation=squarish', score: 760, correctAnswers: 8, totalQuestions: 8, time: '2m 50s' },
      { position: 2, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-carlos-mendoza-4&orientation=squarish', score: 710, correctAnswers: 7, totalQuestions: 8, time: '3m 40s' },
      { position: 3, name: 'Laura Sánchez', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20professional%20Latina%20woman%2C%20warm%20expression%2C%20studio%20portrait%20with%20soft%20shadows%2C%20minimal%20aesthetic%2C%20headshot&width=100&height=100&seq=winner-laura-sanchez-2&orientation=squarish', score: 640, correctAnswers: 6, totalQuestions: 8, time: '5m 10s' },
    ],
    score: { participate: 25, win: 350, share: 15, invite: 25 },
    tags: ['programa', 'colección', 'competencia'],
  },
  {
    id: 'trivia-020',
    title: 'Flash Trivia: Adiviná la Canción',
    description: 'Te damos una frase de la letra, vos elegís la canción. 8 preguntas ultrarrápidas. ¿Reconocés los hits? Solamente 10 segundos por pregunta.',
    type: 'music',
    mode: 'quick',
    status: 'completed',
    startDate: '2026-06-15T19:00:00Z',
    endDate: '2026-06-15T19:20:00Z',
    entityName: 'La Kalle 99.9',
    entityLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=trivia-logo-la-kalle&orientation=squarish',
    questions: musicQuestions.slice(0, 8),
    participants: 198,
    totalQuestions: 8,
    timePerQuestion: 10,
    prize: 'Insignia Velocidad Musical',
    prizeDescription: 'Ganador: Insignia "Velocidad Musical" + 250 pts RFW.',
    topWinners: [
      { position: 1, name: 'Carlos Mendoza', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20portrait%2C%20warm%20smile%2C%20casual%20studio%20setting%2C%20golden%20hour%20lighting%2C%20clean%20background%2C%20professional%20headshot&width=100&height=100&seq=winner-carlos-mendoza-5&orientation=squarish', score: 780, correctAnswers: 8, totalQuestions: 8, time: '0m 52s' },
      { position: 2, name: 'María García', avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%2C%20confident%20smile%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20professional%20headshot%20photography&width=100&height=100&seq=winner-maria-garcia-4&orientation=squarish', score: 720, correctAnswers: 7, totalQuestions: 8, time: '1m 10s' },
      { position: 3, name: 'Pedro López', avatarUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20casual%20portrait%2C%20genuine%20smile%2C%20soft%20natural%20window%20light%2C%20relaxed%20atmosphere%2C%20lifestyle%20photography&width=100&height=100&seq=winner-pedro-lopez-2&orientation=squarish', score: 650, correctAnswers: 6, totalQuestions: 8, time: '1m 25s' },
    ],
    score: { participate: 25, win: 250, share: 15, invite: 25 },
    tags: ['música', 'rápida', 'flash'],
  },
];

// ─── HELPERS ──────────────────────────────────────────────

export function getAllTrivias(): Trivia[] {
  return rfwTrivias;
}

export function getTriviasByType(type: TriviaType | 'todos'): Trivia[] {
  if (type === 'todos') return rfwTrivias;
  return rfwTrivias.filter((t) => t.type === type);
}

export function getTriviasByMode(mode: TriviaMode | 'todos'): Trivia[] {
  if (mode === 'todos') return rfwTrivias;
  return rfwTrivias.filter((t) => t.mode === mode);
}

export function getTriviasByStatus(status: TriviaStatus | 'todos'): Trivia[] {
  if (status === 'todos') return rfwTrivias;
  return rfwTrivias.filter((t) => t.status === status);
}

export function getActiveTrivias(): Trivia[] {
  return rfwTrivias.filter((t) => t.status === 'active' || t.status === 'live');
}

export function getUpcomingTrivias(): Trivia[] {
  return rfwTrivias.filter((t) => t.status === 'upcoming');
}

export function getCompletedTrivias(): Trivia[] {
  return rfwTrivias.filter((t) => t.status === 'completed');
}

export function getTriviaById(id: string): Trivia | undefined {
  return rfwTrivias.find((t) => t.id === id);
}

export function getRecentWinners(): TriviaWinner[] {
  const completed = getCompletedTrivias();
  const winners: TriviaWinner[] = [];
  completed.forEach((t) => {
    t.topWinners.forEach((w) => {
      winners.push({ ...w });
    });
  });
  return winners.slice(0, 12);
}

export function getLatestCompletedTrivias(): Trivia[] {
  return [...getCompletedTrivias()]
    .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
    .slice(0, 8);
}

export function formatTriviaDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatTriviaDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function formatCount(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function getTimeRemainingText(endDate: string): string {
  const now = new Date();
  const target = new Date(endDate);
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'Finalizada';
  const diffMin = Math.ceil(diffMs / (1000 * 60));
  const diffHrs = Math.ceil(diffMs / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffMin < 60) return `${diffMin} min`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays === 1) return 'Mañana';
  if (diffDays <= 3) return `${diffDays} días`;
  return formatTriviaDateShort(endDate);
}

export function getTriviaDifficultyLabel(difficulty: TriviaDifficulty): string {
  const labels: Record<TriviaDifficulty, string> = {
    easy: 'Fácil',
    medium: 'Medio',
    hard: 'Difícil',
  };
  return labels[difficulty];
}

export function getTriviaDifficultyColor(difficulty: TriviaDifficulty): string {
  const colors: Record<TriviaDifficulty, string> = {
    easy: 'bg-emerald-100 text-emerald-700',
    medium: 'bg-amber-100 text-amber-700',
    hard: 'bg-red-100 text-red-700',
  };
  return colors[difficulty];
}